import { Link } from 'react-router-dom';

import { StatusChip } from '@/design';
import { listsApi } from '../../api/lists-api';
import type { IListRow } from '../../api/lists-contracts';
import { useAdminData } from '../../components/admin-page';
import { recordSurveyPath, type RecordArea } from '../../components/participant-link';
import { DD_STEP_COLUMNS, ddStatusChip, ddStep, ddStepStateOf } from '../../dd';
import { ALL_SURVEYS, LIST_PAGE_SIZES, LIST_SURVEYS } from '../../lists';
import { ddAnchor, type IDdRevealState } from './due-diligence';

/**
 * The record card's DD chip (SR-525, C4): one survey's due diligence in a few words — "DD: In
 * progress · Round 1 today 5:00 PM" — beside the survey in the summary's Surveys line, a link to
 * that survey tab's Due diligence block (`IDdRevealState`), so the block at the bottom of a long
 * tab is one press from the card. The words are `ddStep`'s, the queue's *Next step* and the
 * panel's button, so the three never disagree.
 *
 * **One read, the Lists row's DD cells**: a `POST /lists/rows` for the record's participant over
 * the surveys it holds that Lists reads, under `internal_fields.read` (the DD block's own
 * permission), again on the page's write cue. A survey drawn with no DD under way — no interview,
 * no outreach, no booking — draws no chip: whether the advisor is on the DD list is the seeded
 * view's to say, never evaluated here. It sits in a line the card already draws, so the card keeps
 * its height on a desktop.
 */

/** Each survey's DD row of the participant, by survey code; null until read, or where it failed. */
export function useDdRows(
  participantId: string,
  surveys: readonly string[],
  written: number,
  enabled: boolean
): Nullable<ReadonlyMap<string, IListRow>> {
  const codes = LIST_SURVEYS.map((survey) => survey.code).filter((code) => surveys.includes(code));
  const read = useAdminData(
    async (signal) => {
      if (!enabled || codes.length === 0) return new Map<string, IListRow>();
      const answer = await listsApi.rows(
        {
          query: {
            survey: codes.length === 1 ? codes[0] : ALL_SURVEYS.code,
            ...(codes.length > 1 ? { surveys: codes } : {}),
            columns: DD_STEP_COLUMNS,
            criteria: {
              version: 1,
              op: 'and',
              children: [{ field: 'sys.participant_id', op: 'eq', value: participantId }]
            }
          },
          page: 1,
          // survey-api's least page: a row per survey, far fewer.
          pageSize: LIST_PAGE_SIZES[0]
        },
        signal
      );
      return new Map(answer.rows.map((row) => [row.survey, row]));
    },
    [participantId, codes.join('+'), written, enabled]
  );
  return read.data;
}

/** One survey's DD chip, nothing while its DD is not under way. */
export function DdChip({
  row,
  participantId,
  surveyCode,
  area,
  search
}: {
  row: IListRow | undefined;
  participantId: string;
  surveyCode: string;
  area: RecordArea;
  search: string;
}) {
  if (!row) return null;
  const state = ddStepStateOf(row);
  if (state.status === 'none' && state.outreach === 0 && !state.scheduledAt && !state.nextMeeting) return null;
  const step = ddStep(state);
  const chip = ddStatusChip(state.status);
  // A completed DD call with nothing booked says its status alone.
  const words = step.action === 'schedule_meeting' ? chip.label : `${chip.label} · ${step.summary}`;
  return (
    <Link
      to={`${recordSurveyPath(participantId, surveyCode, area)}${search}`}
      state={{ revealDd: ddAnchor(participantId, surveyCode) } satisfies IDdRevealState}
      aria-label={`Due diligence: ${words}${step.overdue ? ', overdue' : ''}`}
      data-dd-chip={step.action}
      className="max-w-full rounded-full outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      <StatusChip tone={step.overdue ? 'danger' : chip.tone} dot>
        DD: {words}
        {step.overdue && ' · overdue'}
      </StatusChip>
    </Link>
  );
}
